import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FaPalette, FaTimes, FaUndo, FaSun, FaMoon } from 'react-icons/fa';
import { useDynamicTheme } from '../contexts/ThemeContext';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.$show ? 1 : 0};
  visibility: ${props => props.$show ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const PickerContainer = styled.div`
  background: ${props => props.theme.colors.background.card};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  border: 1px solid ${props => props.theme.colors.border};
  box-shadow: ${props => props.theme.shadows.lg};
  max-width: 400px;
  width: 90%;
  transform: ${props => props.$show ? 'scale(1)' : 'scale(0.9)'};
  transition: transform 0.3s ease;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const Title = styled.h3`
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.text.muted};
  cursor: pointer;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.circle};
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    color: ${props => props.theme.colors.text.primary};
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const ThemeToggle = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
  padding: ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.primaryAlpha(0.1)};
  border-radius: ${props => props.theme.borderRadius.md};
`;

const ThemeToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  background: ${props => props.$active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.$active ? props.theme.colors.text.primary : props.theme.colors.text.muted};
  border: 1px solid ${props => props.$active ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: 500;

  &:hover {
    background: ${props => props.$active ? props.theme.colors.secondary : props.theme.colors.primaryAlpha(0.1)};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ThemeLabel = styled.div`
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: 500;
`;

const ColorInput = styled.input`
  width: 100%;
  height: 60px;
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  margin-bottom: ${props => props.theme.spacing.lg};
  
  &::-webkit-color-swatch-wrapper {
    padding: 0;
    border-radius: ${props => props.theme.borderRadius.md};
  }
  
  &::-webkit-color-swatch {
    border: none;
    border-radius: ${props => props.theme.borderRadius.md};
  }
`;

const PresetColors = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const PresetColor = styled.button`
  width: 40px;
  height: 40px;
  border-radius: ${props => props.theme.borderRadius.circle};
  border: 2px solid ${props => props.selected ? props.theme.colors.text.primary : 'transparent'};
  background-color: ${props => props.color};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  position: relative;

  &:hover {
    transform: scale(1.1);
    border-color: ${props => props.theme.colors.text.primary};
  }

  &::after {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: ${props => props.theme.borderRadius.circle};
    background: linear-gradient(45deg, ${props => props.color}, transparent);
    z-index: -1;
    opacity: ${props => props.selected ? 0.3 : 0};
    transition: opacity ${props => props.theme.transitions.fast};
  }
`;

const CurrentColorPreview = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
  padding: ${props => props.theme.spacing.md};
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: ${props => props.theme.borderRadius.md};
`;

const ColorSwatch = styled.div`
  width: 30px;
  height: 30px;
  border-radius: ${props => props.theme.borderRadius.circle};
  background-color: ${props => props.color};
  border: 2px solid ${props => props.theme.colors.border};
`;

const ColorInfo = styled.div`
  flex: 1;
`;

const ColorLabel = styled.div`
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.fontSize.sm};
`;

const ColorValue = styled.div`
  color: ${props => props.theme.colors.text.primary};
  font-family: monospace;
  font-weight: 600;
`;

const Actions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  font-weight: 600;
  transition: all ${props => props.theme.transitions.fast};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};

  ${props => props.variant === 'primary' ? `
    background: ${props.theme.colors.primary};
    color: ${props.theme.colors.text.primary};
    
    &:hover {
      background: ${props.theme.colors.secondary};
      transform: translateY(-1px);
    }
  ` : `
    background: transparent;
    color: ${props.theme.colors.text.muted};
    border: 1px solid ${props.theme.colors.border};
    
    &:hover {
      color: ${props.theme.colors.text.primary};
      border-color: ${props.theme.colors.text.primary};
    }
  `}
`;

// Preset color palette
const presetColors = [
  '#7B2CBF', // Default purple
  '#FF6B6B', // Red
  '#4ECDC4', // Teal
  '#45B7D1', // Blue
  '#96CEB4', // Green
  '#FFEAA7', // Yellow
  '#DDA0DD', // Plum
  '#98D8C8', // Mint
  '#F7DC6F', // Gold
  '#BB8FCE', // Lavender
  '#85C1E9', // Sky blue
  '#F8C471', // Orange
];

const ColorPicker = ({ show, onClose }) => {
  const { mainColor, isDark, updateMainColor, toggleTheme, resetToDefault } = useDynamicTheme();
  const [tempColor, setTempColor] = useState(mainColor);
  const overlayRef = useRef(null);

  useEffect(() => {
    setTempColor(mainColor);
  }, [mainColor, show]);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  };

  const handleColorChange = (color) => {
    setTempColor(color);
  };

  const handleApply = () => {
    updateMainColor(tempColor);
    onClose();
  };

  const handleReset = () => {
    resetToDefault();
    setTempColor('#7B2CBF');
  };

  const handlePresetClick = (color) => {
    setTempColor(color);
  };

  return (
    <Overlay 
      $show={show} 
      ref={overlayRef} 
      onClick={handleOverlayClick}
    >
      <PickerContainer $show={show}>
        <Header>
          <Title>
            <FaPalette />
            Theme Customization
          </Title>
          <CloseButton onClick={onClose}>
            <FaTimes />
          </CloseButton>
        </Header>

        <ThemeToggle>
          <ThemeLabel>Theme Mode:</ThemeLabel>
          <ThemeToggleButton 
            $active={isDark} 
            onClick={() => toggleTheme()}
          >
            <FaMoon />
            Dark
          </ThemeToggleButton>
          <ThemeToggleButton 
            $active={!isDark} 
            onClick={() => toggleTheme()}
          >
            <FaSun />
            Light
          </ThemeToggleButton>
        </ThemeToggle>

        <CurrentColorPreview>
          <ColorSwatch color={tempColor} />
          <ColorInfo>
            <ColorLabel>Current Color</ColorLabel>
            <ColorValue>{tempColor.toUpperCase()}</ColorValue>
          </ColorInfo>
        </CurrentColorPreview>

        <ColorInput
          type="color"
          value={tempColor}
          onChange={(e) => handleColorChange(e.target.value)}
        />

        <PresetColors>
          {presetColors.map((color) => (
            <PresetColor
              key={color}
              color={color}
              selected={tempColor.toLowerCase() === color.toLowerCase()}
              onClick={() => handlePresetClick(color)}
            />
          ))}
        </PresetColors>

        <Actions>
          <ActionButton onClick={handleReset}>
            <FaUndo />
            Reset All
          </ActionButton>
          <ActionButton variant="primary" onClick={handleApply}>
            Apply
          </ActionButton>
        </Actions>
      </PickerContainer>
    </Overlay>
  );
};

export default ColorPicker; 